import { CountAddItem, CountAddProps, CountOrder, WLocation } from "@/types/inventoryV2";
import { Button, Flex, Modal, Spin, Upload, App, notification, Progress, Checkbox, Input } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ColumnsType } from "antd/es/table";
import { generateTemple, ParseResult, parseExcelFile, ValiedRaeseResult } from "./utils";
import SText from "@/styles/SText";
import { addLocation, locationList, countAdd as countAddApi, countDo } from "@/apis/inventoryv2";
import { getLocationIdx } from "@/utils/wLocation";
import STableLayout from "@/styles/STableLayout";
import { AnyObject } from "antd/es/_util/type";

interface BatchImportProps {
  onSuccess?: (orders: CountOrder[]) => void
}

interface LocationTempleData extends ValiedRaeseResult {
  location: WLocation | null;
}

const BatchImport: FC<BatchImportProps> = ({ onSuccess }) => {
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const [open, setOpen] = useState<boolean>(false);
  const [templeData, setTempleData] = useState<Array<ValiedRaeseResult>>([]);
  const [uploading, setUploading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [processed, setProcessed] = useState<number>(0);
  const [allNum, setAllNum] = useState<number>(0);
  const [noLotChecked, setNoLotChecked] = useState<0 | 1>(0);
  const [isPreData, setIsPreData] = useState<boolean>(false);
  const [desc, setDesc] = useState<string>('');

  const countAdd = async () => {
    if (templeData.length <= 0) return notification.error({ placement: 'top', message: t('bom.errTip') })
    setProcessed(0);
    setAllNum(0);
    setLoading(true)
    let countingData: LocationTempleData[] = [];
    let errLocations: string[] = [];
    let preOutLocations: string[] = [];
    setIsPreData(true)
    for (let i = 0; i < templeData.length; i++) {
      try {
        let location = (await locationList({ body: templeData[i].locationIdx })).body?.[0] || null;
        if (!!!location) {
          let [l1, l2, l3, l4, l5] = templeData[i].locationIdx.split("-")
          // 添加库位
          location = await addLocation({
            l1: Number(l1),
            l2: Number(l2) ?? null,
            l3: Number(l3) ?? null,
            l4: Number(l4) ?? null,
            l5: Number(l5) ?? null,
            label1: templeData[i].label1,
            type: 1
          })
        }
        countingData.push({
          ...templeData[i],
          location,
        })
      } catch (error) {
        console.log(">>>>>ERR", error);
        errLocations.push(templeData[i].locationIdx)
        countingData.push({
          ...templeData[i],
          location: null
        })
      }
    }
    setIsPreData(false)
    // 过滤出有效库位的数据
    const validData = countingData.filter(data => !!data.location);

    // 按库位分组
    const locationMap = new Map<string, LocationTempleData[]>();
    validData.forEach(data => {
      const locationId = data.location!.id;
      if (!locationMap.has(locationId)) {
        locationMap.set(locationId, []);
      }
      locationMap.get(locationId)!.push(data);
    });

    // 构建 CountAddProps 数组
    const countAdds: CountAddProps[] = [];
    locationMap.forEach((items, locationId) => {
      const countItems: CountAddItem[] = items.map(item => ({
        lotStr: '', // 模板中没有批次字段，默认为空
        productCode: item.productCode,
        counting: item.countedNum,
        barcodes: [], // 模板中没有条码字段，默认为空数组
      }));

      countAdds.push({
        locationId,
        desc,
        items: countItems,
      });
    });

    let orders: CountOrder[] = [];
    let errorOrder: string[] = [];
    let errProductCodes: string[] = [];
    setAllNum(countAdds.length)
    // 处理每个 CountAddProps
    for (let j = 0; j < countAdds.length; j++) {
      let currentCountAdd = countAdds[j];
      let hasError = true;

      // 循环处理，直到 items 为空或不再出现 70605 错误
      while (currentCountAdd.items.length > 0 && hasError) {
        try {
          let countOrder = await countAddApi(currentCountAdd);
          if (countOrder) countOrder = await countDo(countOrder.id, noLotChecked);
          orders.push(countOrder);
          hasError = false;
        } catch (error: any) {
          if (error === 70605) {
            // 记录错误的 productCode
            const failedProductCode = error?.extra;
            errorOrder.push(failedProductCode);

            // 从 items 中移除对应的项
            currentCountAdd.items = currentCountAdd.items.filter(
              item => item.productCode !== failedProductCode
            );

            // 如果 items 为空了，退出循环
            if (currentCountAdd.items.length === 0) {
              hasError = false;
            }
            // 继续循环，尝试提交剩余的 items
          } else if (error === 73003) {
            // 其他错误，直接退出
            hasError = false;
            let location = locationMap.get(currentCountAdd.locationId)?.[0]?.location
            location && preOutLocations.push(getLocationIdx(location))
          } else {
            // 其他错误，直接退出
            hasError = false;
            if (error?.code === 70605) {
              const failedProductCode = String(error?.extra || error?.msg?.replace('product code not existed.', '') || '');
              failedProductCode && errProductCodes.push(failedProductCode);
            }
            let location = locationMap.get(currentCountAdd.locationId)?.[0]?.location
            location && errLocations.push(getLocationIdx(location))
          }
        }
      }
      setProcessed(prev => prev + 1)
    }

    if (errLocations.length > 0 || errorOrder.length > 0 || preOutLocations.length > 0) {
      notification.warning({
        placement: 'top',
        message: t('bom.errTip'),
        description: <>
          {errLocations.length > 0 && <div>
            {t('counting.locationParseFailed', { codes: errProductCodes.join(', '), locations: errLocations.join(', ') })}
          </div>}
          {preOutLocations.length > 0 && <div>
            {t('counting.preOutExt')}：{preOutLocations.join(', ')}
          </div>}
          {errorOrder.length > 0 && <div>
            {t('counting.productCodeNotExisted', { codes: errorOrder.join(', ') })}
          </div>}
        </>,
        duration: 0
      });
    }

    setLoading(false);
    if (orders.length > 0) {
      onSuccess?.(orders);
      setTempleData([]);
      setDesc('');
      setOpen(false);
    }
  }

  // 表格列定义
  const columns: ColumnsType<ValiedRaeseResult> = [
    { title: t('counting.productCode'), dataIndex: 'productCode', key: 'productCode', width: 120, sorter: (a, b) => a.productCode.localeCompare(b.productCode) },
    { title: t('counting.productName'), dataIndex: 'productName', key: 'productName', width: 150 },
    { title: t('counting.productModal'), dataIndex: 'productModal', key: 'productModal', width: 120, render: (_, record) => (<SText text={record?.productModal ?? ''} />) },
    { title: t('counting.productUom'), dataIndex: 'productUom', key: 'productUom', width: 80 },
    { title: t('counting.bookNum'), dataIndex: 'bookNum', key: 'bookNum', width: 100 },
    { title: t('counting.countedNum'), dataIndex: 'countedNum', key: 'countedNum', width: 100 },
    { title: t('counting.locationIdx'), dataIndex: 'locationIdx', key: 'locationIdx', width: 120, sorter: (a, b) => a.locationIdx.localeCompare(b.locationIdx) },
    { title: t('warehouse.label1'), dataIndex: 'label1', key: 'label1', width: 120 },
    {
      title: t('counting.opt'),
      key: 'action',
      width: 60,
      fixed: "right",
      render: (_, __, index) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleDeleteRow(index)}
        />
      ),
    },
  ];


  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const parseResult: ParseResult = await parseExcelFile(file, t);
      setTempleData(parseResult.validData);

      // 根据是否有过滤数据选择不同的提示文案
      if (parseResult.filteredData.length > 0) {
        // 有过滤数据
        const filteredRows = parseResult.filteredData
          .map(item => item.rowIndex)
        notification.warning({
          placement: 'top',
          message: t('order.operationSucc'),
          description: t('counting.parseDataFilter', {
            count: parseResult.validData.length,
            rows: filteredRows.join(', ')
          }),
          duration: 10,
        });
      } else {
        // 没有过滤数据
        notification.success({
          placement: 'top',
          message: t('order.operationSucc'),
          description: t('counting.parseDataSuccess', { count: parseResult.validData.length }),
        });
      }
    } catch {
      notification.error({
        placement: 'top',
        message: t('order.optErr'),
        description: t('bom.errTip'),
      });
    } finally {
      setUploading(false);
    }
    return false; // 阻止自动上传
  };

  // 关闭Modal时的确认
  const handleCancel = () => {
    if (templeData.length > 0) {
      modal.confirm({
        title: t('counting.confirmCloseImport'),
        content: t('counting.confirmCloseImportContent'),
        okText: t('common.confirm'),
        cancelText: t('common.cancel'),
        onOk: () => {
          setTempleData([]);
          setDesc('');
          setOpen(false);
        },
      });
    } else {
      setDesc('');
      setOpen(false);
    }
  };

  // 删除行时的确认
  const handleDeleteRow = (index: number) => {
    modal.confirm({
      title: t('counting.confirmDeleteRow'),
      content: t('counting.confirmDeleteRowContent'),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      onOk: () => {
        setTempleData(last => last.filter((_, i) => i !== index));
      },
    });
  };

  return (
    <>
      <Button onClick={() => setOpen(true)}>{t('counting.batchImport')}</Button>
      <Modal open={open} onCancel={handleCancel} maskClosable={false} width={1200}
        centered
        title={
          <Flex gap={10} align="center" justify="flex-start" >
            <SText text={t('counting.startInv')} style={{ width: "unset" }} />
            {/* <Checkbox
              checked={noLotChecked === 1}
              onChange={(e) => setNoLotChecked(e.target.checked ? 1 : 0)}
            >
              {t('counting.noLot')}
            </Checkbox> */}
          </Flex >
        }

        okButtonProps={{ loading: loading || uploading }}
        cancelButtonProps={{ disabled: loading || uploading }}
        closable={!loading}
        okText={t('counting.confirmImport')}
        onOk={() => countAdd()}
      >
        <Spin spinning={loading}>
          <Flex vertical style={{ width: "100%", height: "70vh", marginBottom: -34 }} >
            <Flex justify="space-between" style={{ marginBottom: 10 }}>
              <Upload
                accept=".xlsx,.xls"
                beforeUpload={handleUpload}
                showUploadList={false}
                disabled={uploading}
              >
                <Button type="primary" loading={uploading}>{t('counting.importData')}</Button>
              </Upload>
              <Button type="link" onClick={() => generateTemple(t)}>{t('counting.downloadTemplate')}</Button>
            </Flex>
            <Flex vertical gap={6} style={{ marginBottom: 20 }}>
              <span>{t('purchase.remark')}</span>
              <Input.TextArea
                rows={2}
                value={desc}
                placeholder={t('counting.inputDesc')}
                onChange={(e) => setDesc(e.target.value)}
                maxLength={500}
                showCount
              />
            </Flex>
            <STableLayout
              rowKey={(record, index) => `${record.productCode}-${index}`}
              columns={columns as Array<AnyObject>}
              dataSource={templeData}
              headerChildren={null}
              total={templeData.length}
            />
          </Flex>
        </Spin>
      </Modal>
      {
        loading && <Modal title={t('counting.batchImport') + "..."} closable={false} footer={null} maskClosable={false} open={loading} centered width={1000} >
          <Flex style={{ marginTop: 120, marginBottom: 100 }} justify="center" >
            <Spin spinning={loading} size="large" />
          </Flex>
          {
            isPreData ? <span>{t('counting.initLoc')}</span>
              : <>
                <Progress percent={Number((processed / allNum * 100).toFixed(2))} />
                <span>{`${t('counting.batchImport')}：${processed}/${allNum}`}</span>
              </>
          }
        </Modal>
      }
    </>
  )
};

export default BatchImport;
